import {useMsal} from "@azure/msal-react";
import {useErrorReporter} from "../general/ErrorViewer";
import {AccountInfo, BrowserAuthError, InteractionRequiredAuthError} from "@azure/msal-browser";
import {useCallback, useMemo} from "react";
import {AuthSetup, getRedirectUri} from "../authConfig";
import {NeedsReLoginError} from "./AuthHook";

export const useAzureAuth = (authSetup: AuthSetup) => {
    const {instance, accounts} = useMsal();
    const reportError = useErrorReporter();

    function dependOn(ignored: AccountInfo[]) {
        // add noop of an implicit dependency, like accounts. Do not remove!
    }

    const getToken = useCallback(async () => {
        dependOn(accounts);
        const account = instance.getActiveAccount();
        if (account == null) {
            throw new Error("No accounts found");
        }
        try {
            const response = await instance.acquireTokenSilent({
                ...authSetup.loginRequest,
                account: account,
            });
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
                throw new NeedsReLoginError();
            }
            throw error;
        }
    }, [instance, accounts, authSetup]);

    const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
        const token = await getToken();
        const headers: Record<string, string> = {};
        headers["Authorization"] = `Bearer ${token}`
        return headers;
    }, [getToken]);

    const isLoggedIn = useCallback(() => {
        dependOn(accounts);
        return instance.getActiveAccount() != null;
    }, [instance, accounts]);

    const getActiveAccount = useCallback(() => {
        return instance.getActiveAccount();
    }, [instance]);

    const loginPopup = useCallback(async () => {
        try {
            const res = await instance.loginPopup({
                ...authSetup.loginRequest,
                prompt: "login",
                redirectUri: getRedirectUri(authSetup)
            })
            instance.setActiveAccount(res.account)
        } catch (error) {
            console.log(`Error during login popup: ${error}`)
            reportError(`${error}`);
        }
    }, [instance, reportError, authSetup]);

    const logout = useCallback(async () => {
        if (instance.getActiveAccount() != null) {
            await instance.logoutPopup({
                account: instance.getActiveAccount(),
                postLogoutRedirectUri: getRedirectUri(authSetup)
            });
            instance.setActiveAccount(null);
        }
    }, [instance, authSetup]);

    const getUserId = useCallback(async () => {
        dependOn(accounts);
        return instance.getActiveAccount()?.localAccountId;
    }, [instance, accounts]);

    const refreshToken = useCallback(async () => {
        await getToken();
    }, [getToken]);

    return useMemo(() => {
        return {
            getToken,
            getHeaders,
            isLoggedIn,
            getActiveAccount,
            loginPopup,
            logout,
            canLogout: true,
            getUserId,
            refreshToken
        }
    }, [getToken, getHeaders, isLoggedIn, getActiveAccount, loginPopup, logout, getUserId, refreshToken]);
}